import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  FileText,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  FolderCog,
  X,
  Check,
  GripVertical,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Checkbox } from '../components/ui/checkbox';

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'location', label: 'Location' },
  { value: 'person', label: 'Person (Salutation + Name)' },
  { value: 'person_with_relation', label: 'Person with Relation' },
  { value: 'survey_block', label: 'Survey Block' },
  { value: 'select', label: 'Select (uses JSON options)' },
];

const AdminDocTypesPage = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    template_paragraph: '',
    fields: [],
    is_active: true,
    allows_ownership_change: false,
    tracing_templates: [],
  });
  const [submitting, setSubmitting] = useState(false);

  // Inline add-category (inside Add/Edit Doc Type modal)
  const [showAddCategoryInline, setShowAddCategoryInline] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Manage Categories dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageNewCategory, setManageNewCategory] = useState('');
  const [manageAdding, setManageAdding] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // Fields editor — advanced JSON toggle
  const [showFieldsJson, setShowFieldsJson] = useState(false);

  // Field row manipulation helpers
  const handleAddField = () => {
    setFormData((prev) => ({
      ...prev,
      fields: [
        ...(prev.fields || []),
        { name: '', label: '', type: 'text', required: false, bold: false },
      ],
    }));
  };

  const handleUpdateField = (index, key, value) => {
    setFormData((prev) => {
      const next = [...(prev.fields || [])];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, fields: next };
    });
  };

  const handleRemoveField = (index) => {
    setFormData((prev) => {
      const next = [...(prev.fields || [])];
      next.splice(index, 1);
      return { ...prev, fields: next };
    });
  };

  const handleMoveField = (index, direction) => {
    setFormData((prev) => {
      const next = [...(prev.fields || [])];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, fields: next };
    });
  };

  useEffect(() => {
    fetchDocTypes();
    fetchCategories();
  }, []);

  const fetchDocTypes = async () => {
    try {
      const response = await api.get('/document-types');
      setDocumentTypes(response.data);
      const expanded = {};
      response.data.forEach((dt) => {
        expanded[dt.category] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to fetch document types');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.category || !formData.category.trim()) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.template_paragraph.trim()) {
      toast.error('Template paragraph is required');
      return;
    }
    setSubmitting(true);

    try {
      if (editingType) {
        await api.put(`/document-types/${editingType.id}`, formData);
        toast.success('Document type updated');
      } else {
        await api.post('/document-types', formData);
        toast.success('Document type created');
      }
      setDialogOpen(false);
      fetchDocTypes();
      fetchCategories();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) return;

    try {
      await api.delete(`/document-types/${id}`);
      toast.success('Document type deleted');
      fetchDocTypes();
    } catch (error) {
      toast.error('Failed to delete document type');
    }
  };

  const openEditDialog = (docType) => {
    setEditingType(docType);
    setFormData({
      name: docType.name,
      category: docType.category,
      template_paragraph: docType.template_paragraph,
      fields: docType.fields,
      is_active: docType.is_active,
      allows_ownership_change: !!docType.allows_ownership_change,
      tracing_templates: docType.tracing_templates || [],
    });
    setShowAddCategoryInline(false);
    setNewCategoryName('');
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      name: '',
      category: '',
      template_paragraph: '',
      fields: [],
      is_active: true,
      allows_ownership_change: false,
      tracing_templates: [],
    });
    setShowAddCategoryInline(false);
    setNewCategoryName('');
  };

  // ----- Tracing Templates management -----
  const addTracingTemplate = () => {
    setFormData((prev) => {
      const list = prev.tracing_templates || [];
      const num = list.length + 1;
      return {
        ...prev,
        tracing_templates: [
          ...list,
          { id: `t${Date.now()}`, name: num === 1 ? 'Template 1 (Default)' : `Template ${num}`, template: '' },
        ],
      };
    });
  };
  const updateTracingTemplate = (idx, key, value) => {
    setFormData((prev) => {
      const list = [...(prev.tracing_templates || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...prev, tracing_templates: list };
    });
  };
  const removeTracingTemplate = (idx) => {
    setFormData((prev) => {
      const list = [...(prev.tracing_templates || [])];
      list.splice(idx, 1);
      return { ...prev, tracing_templates: list };
    });
  };


  // Add category from within Document Type modal
  const handleInlineAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.error('Category name is required');
      return;
    }
    setAddingCategory(true);
    try {
      await api.post('/categories', { name });
      toast.success(`Category "${name}" added`);
      await fetchCategories();
      setFormData((prev) => ({ ...prev, category: name }));
      setNewCategoryName('');
      setShowAddCategoryInline(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  // Manage Categories dialog — add
  const handleManageAddCategory = async () => {
    const name = manageNewCategory.trim();
    if (!name) {
      toast.error('Category name is required');
      return;
    }
    setManageAdding(true);
    try {
      await api.post('/categories', { name });
      toast.success(`Category "${name}" added`);
      await fetchCategories();
      setManageNewCategory('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setManageAdding(false);
    }
  };

  // Manage Categories dialog — delete
  const handleDeleteCategory = async (name) => {
    if (!window.confirm(`Delete category "${name}"? This cannot be undone.`)) return;
    setDeletingCategory(name);
    try {
      await api.delete(`/categories/${encodeURIComponent(name)}`);
      toast.success(`Category "${name}" deleted`);
      await fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setDeletingCategory(null);
    }
  };

  // Compute doc type count per category (for in-use detection)
  const docTypeCountByCategory = documentTypes.reduce((acc, dt) => {
    acc[dt.category] = (acc[dt.category] || 0) + 1;
    return acc;
  }, {});

  // Group by category — fall back to "Uncategorized" so nothing is hidden
  const groupedTypes = documentTypes.reduce((acc, dt) => {
    const cat = (dt.category || '').trim() || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(dt);
    return acc;
  }, {});

  const filteredGroups = Object.entries(groupedTypes).reduce((acc, [category, types]) => {
    const filtered = types.filter(
      (dt) =>
        dt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-doc-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold text-[#1e3a5f]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Document Types
          </h1>
          <p className="text-slate-500 mt-1">
            Manage DDR document templates ({documentTypes.length} types, {categories.length} categories)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setManageDialogOpen(true)}
            data-testid="manage-categories-btn"
          >
            <FolderCog size={18} className="mr-2" />
            Manage Categories
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="add-doc-type-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Document Type
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={20}
            />
            <Input
              placeholder="Search document types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-doc-types"
            />
          </div>
        </CardContent>
      </Card>

      {/* Document Types by Category */}
      <div className="space-y-4">
        {Object.entries(filteredGroups).map(([category, types]) => (
          <Collapsible
            key={category}
            open={expandedCategories[category]}
            onOpenChange={(open) =>
              setExpandedCategories((prev) => ({ ...prev, [category]: open }))
            }
          >
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between py-4 hover:bg-slate-50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    {expandedCategories[category] ? (
                      <ChevronDown size={20} className="text-slate-400" />
                    ) : (
                      <ChevronRight size={20} className="text-slate-400" />
                    )}
                    <CardTitle className="text-lg text-[#1e3a5f]">{category}</CardTitle>
                    <span className="text-sm text-slate-400">({types.length})</span>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="divide-y divide-slate-100">
                    {types.map((docType) => (
                      <div
                        key={docType.id}
                        className="flex items-center justify-between py-3"
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">{docType.name}</p>
                            <p className="text-xs text-slate-400">
                              {docType.fields?.length || 0} fields
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(docType)}
                            data-testid={`edit-doc-type-${docType.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(docType.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-doc-type-${docType.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>

      {/* Add/Edit Document Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingType ? 'Edit Document Type' : 'Add Document Type'}
            </DialogTitle>
            <DialogDescription>
              Configure the document type and its template.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Sale Deed"
                    required
                    data-testid="doc-type-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Category *</Label>
                    {!showAddCategoryInline && (
                      <button
                        type="button"
                        onClick={() => setShowAddCategoryInline(true)}
                        className="text-xs text-[#1e3a5f] hover:underline flex items-center gap-1"
                        data-testid="inline-add-category-btn"
                      >
                        <Plus size={12} /> Add Category
                      </button>
                    )}
                  </div>

                  {showAddCategoryInline ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="New category name"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleInlineAddCategory();
                          }
                          if (e.key === 'Escape') {
                            setShowAddCategoryInline(false);
                            setNewCategoryName('');
                          }
                        }}
                        data-testid="inline-new-category-input"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={handleInlineAddCategory}
                        disabled={addingCategory}
                        className="text-green-600 hover:text-green-700"
                        data-testid="inline-confirm-category-btn"
                      >
                        <Check size={16} />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setShowAddCategoryInline(false);
                          setNewCategoryName('');
                        }}
                        className="text-slate-500"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={formData.category}
                      onValueChange={(value) =>
                        setFormData({ ...formData, category: value })
                      }
                      required
                    >
                      <SelectTrigger data-testid="doc-type-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-400">
                            No categories yet. Click "Add Category" above.
                          </div>
                        ) : (
                          categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Template Paragraph *</Label>
                <Textarea
                  value={formData.template_paragraph}
                  onChange={(e) =>
                    setFormData({ ...formData, template_paragraph: e.target.value })
                  }
                  placeholder="Use XXXX_fieldname for placeholders..."
                  rows={4}
                  required
                />
                <p className="text-xs text-slate-400">
                  Use XXXX_fieldname format for placeholders (e.g., XXXX_survey_no, XXXX_village)
                </p>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={!!formData.allows_ownership_change}
                    onCheckedChange={(val) =>
                      setFormData({ ...formData, allows_ownership_change: !!val })
                    }
                    data-testid="allows-ownership-change-checkbox"
                    className="mt-0.5"
                  />
                  <div className="text-sm">
                    <span className="font-medium text-slate-800">Allows Ownership Change</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Enable for documents that legally transfer ownership (Deeds, Mutation
                      Register, RR Pakka Book). When disabled, an owner-name mismatch on this
                      document triggers a continuity warning.
                    </p>
                  </div>
                </label>
              </div>

              {/* Tracing Templates (II. Tracing of Title) */}
              <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/40 p-3" data-testid="tracing-templates-editor">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Tracing Templates</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Paragraph templates for the "II. Tracing of Title" section. First = default.
                      Use <code className="text-[#1e3a5f]">{'{field_name}'}</code> or <code className="text-[#1e3a5f]">XXXX_field_name</code>,
                      and the reserved <code className="text-[#1e3a5f]">{'{trace_doc_no}'}</code> for the live document number.
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addTracingTemplate} data-testid="add-tracing-template-btn">
                    <Plus size={14} className="mr-1" /> Add Template
                  </Button>
                </div>

                {(!formData.tracing_templates || formData.tracing_templates.length === 0) && (
                  <p className="text-xs text-slate-400 italic px-1">
                    No tracing templates yet. This document won't appear in the Tracing of Title section until you add one.
                  </p>
                )}

                <div className="space-y-2">
                  {(formData.tracing_templates || []).map((tmpl, index) => (
                    <div key={tmpl.id || index} className="border border-slate-200 rounded-md p-3 bg-white space-y-2" data-testid={`tracing-template-row-${index}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={tmpl.name || ''}
                          onChange={(e) => updateTracingTemplate(index, 'name', e.target.value)}
                          placeholder={`Template ${index + 1} name`}
                          className="flex-1"
                          data-testid={`tracing-template-name-${index}`}
                        />
                        <Button
                          type="button" size="icon" variant="ghost"
                          onClick={() => removeTracingTemplate(index)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-tracing-template-${index}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <Textarea
                        value={tmpl.template || ''}
                        onChange={(e) => updateTracingTemplate(index, 'template', e.target.value)}
                        placeholder="e.g. Vide Sale Deed (Document No. {trace_doc_no}) dated {date}, {in_favour_of} acquired title over Sy. No. {new_survey_number} from {executed_by}."
                        rows={3}
                        data-testid={`tracing-template-text-${index}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Fields</Label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowFieldsJson((v) => !v)}
                      className="text-xs text-slate-500 hover:text-[#1e3a5f] hover:underline"
                      data-testid="toggle-fields-json-btn"
                    >
                      {showFieldsJson ? 'Hide JSON' : 'Advanced (JSON)'}
                    </button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddField}
                      data-testid="add-field-btn"
                    >
                      <Plus size={14} className="mr-1" /> Add Field
                    </Button>
                  </div>
                </div>

                {(!formData.fields || formData.fields.length === 0) && (
                  <p className="text-xs text-slate-400 italic px-1">
                    No fields yet. Click "Add Field" to define the first one.
                  </p>
                )}

                <div className="space-y-2">
                  {(formData.fields || []).map((field, index) => (
                    <div
                      key={index}
                      className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-2"
                      data-testid={`field-row-${index}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col items-center pt-1 text-slate-300">
                          <GripVertical size={14} />
                          <span className="text-[10px] font-mono">{index + 1}</span>
                        </div>
                        <div className="grid grid-cols-12 gap-2 flex-1">
                          <div className="col-span-4">
                            <Label className="text-xs text-slate-500">Name *</Label>
                            <Input
                              value={field.name || ''}
                              onChange={(e) =>
                                handleUpdateField(index, 'name', e.target.value)
                              }
                              placeholder="e.g., survey_no"
                              className="h-8 text-sm"
                              data-testid={`field-name-${index}`}
                            />
                          </div>
                          <div className="col-span-4">
                            <Label className="text-xs text-slate-500">Label</Label>
                            <Input
                              value={field.label || ''}
                              onChange={(e) =>
                                handleUpdateField(index, 'label', e.target.value)
                              }
                              placeholder="e.g., Survey Number"
                              className="h-8 text-sm"
                              data-testid={`field-label-${index}`}
                            />
                          </div>
                          <div className="col-span-4">
                            <Label className="text-xs text-slate-500">Type</Label>
                            <Select
                              value={field.type || 'text'}
                              onValueChange={(value) =>
                                handleUpdateField(index, 'type', value)
                              }
                            >
                              <SelectTrigger
                                className="h-8 text-sm"
                                data-testid={`field-type-${index}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPE_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveField(index)}
                          className="text-red-500 hover:text-red-700 h-8 w-8"
                          data-testid={`remove-field-${index}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>

                      <div className="flex items-center gap-6 pl-6">
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={!!field.required}
                            onCheckedChange={(val) =>
                              handleUpdateField(index, 'required', !!val)
                            }
                            data-testid={`field-required-${index}`}
                          />
                          Required
                        </label>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={!!field.bold}
                            onCheckedChange={(val) =>
                              handleUpdateField(index, 'bold', !!val)
                            }
                            data-testid={`field-bold-${index}`}
                          />
                          <span>
                            Bold in output
                            <span className="text-slate-400 ml-1">
                              (renders value bold in Preview, PDF & Word)
                            </span>
                          </span>
                        </label>
                        <div className="ml-auto flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMoveField(index, -1)}
                            disabled={index === 0}
                            className="h-7 w-7 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                            data-testid={`field-up-${index}`}
                          >
                            <ChevronDown size={14} className="rotate-180" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMoveField(index, 1)}
                            disabled={index === (formData.fields?.length || 0) - 1}
                            className="h-7 w-7 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                            data-testid={`field-down-${index}`}
                          >
                            <ChevronDown size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {showFieldsJson && (
                  <div className="space-y-1 pt-2">
                    <Label className="text-xs text-slate-500">
                      Raw JSON (for advanced options like <code>select.options</code>)
                    </Label>
                    <Textarea
                      value={JSON.stringify(formData.fields, null, 2)}
                      onChange={(e) => {
                        try {
                          setFormData({
                            ...formData,
                            fields: JSON.parse(e.target.value),
                          });
                        } catch (err) {
                          // Invalid JSON, keep current
                        }
                      }}
                      rows={6}
                      className="font-mono text-xs"
                      data-testid="fields-json-textarea"
                    />
                    <p className="text-[11px] text-slate-400">
                      Editing here will replace the structured view above. Supports custom
                      keys like <code>options</code> for select types.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={submitting}
                data-testid="submit-doc-type-btn"
              >
                {submitting ? 'Saving...' : editingType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={manageDialogOpen} onOpenChange={setManageDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="manage-categories-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Manage Categories
            </DialogTitle>
            <DialogDescription>
              Add new categories or delete unused ones. Categories in use by document types cannot be deleted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add new category */}
            <div className="flex items-center gap-2">
              <Input
                value={manageNewCategory}
                onChange={(e) => setManageNewCategory(e.target.value)}
                placeholder="New category name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleManageAddCategory();
                  }
                }}
                data-testid="manage-new-category-input"
              />
              <Button
                type="button"
                onClick={handleManageAddCategory}
                disabled={manageAdding}
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                data-testid="manage-add-category-btn"
              >
                <Plus size={16} className="mr-1" />
                Add
              </Button>
            </div>

            {/* Category list */}
            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="p-4 text-sm text-slate-400 text-center">
                  No categories yet. Add one above.
                </p>
              ) : (
                categories.map((cat) => {
                  const inUse = (docTypeCountByCategory[cat] || 0) > 0;
                  return (
                    <div
                      key={cat}
                      className="flex items-center justify-between px-4 py-3"
                      data-testid={`category-row-${cat}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">{cat}</p>
                        <p className="text-xs text-slate-400">
                          {inUse
                            ? `${docTypeCountByCategory[cat]} document type(s)`
                            : 'Unused'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCategory(cat)}
                        disabled={inUse || deletingCategory === cat}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30"
                        title={inUse ? 'Cannot delete: category is in use' : 'Delete category'}
                        data-testid={`delete-category-${cat}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setManageDialogOpen(false)}
              data-testid="close-manage-categories-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDocTypesPage;
